import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useMutation } from 'convex/react'
import { api as convexApi } from '../../convex/_generated/api'
import { useMe } from '../../context/MeContext'
import { getConvexToken } from '../../convex'

const STORAGE_KEY = 'loadboard:searchTabs:v3'
export const MAX_TABS = 6
const PERSIST_DEBOUNCE_MS = 400

const emptyFilters = () => ({
	origin: '',
	originLat: null,
	originLng: null,
	originDh: 50,
	destination: '',
	destinationLat: null,
	destinationLng: null,
	destinationDh: 150,
	equipment: '',
	source: 'all',
	sort: 'pickup',
	pickDateFrom: '',
	pickDateTo: '',
	maxWeight: '',
	minDistance: '',
	maxDistance: '',
	lastSeenHours: '',
	showArchive: false,
})

const VALID_EQUIPMENT_KEYS = new Set(['van', 'powerOnly'])

const sanitizeEquipment = v => {
	if (typeof v !== 'string') return ''
	const keys = v
		.split(',')
		.map(s => s.trim())
		.filter(k => VALID_EQUIPMENT_KEYS.has(k))
	return keys.join(',')
}
const VALID_SORT = new Set(['lastSeen', 'pickup', 'pay'])
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/

const toFiniteNumberOrNull = v => {
	if (v == null || v === '') return null
	const n = Number(v)
	return Number.isFinite(n) ? n : null
}

const toNumericStringOrEmpty = v => {
	if (v == null || v === '') return ''
	const n = Number(v)
	return Number.isFinite(n) && n >= 0 ? String(n) : ''
}

const toIsoDateOrEmpty = v => (typeof v === 'string' && ISO_DATE_RE.test(v) ? v : '')

const todayIso = () => {
	const d = new Date()
	const y = d.getFullYear()
	const m = String(d.getMonth() + 1).padStart(2, '0')
	const day = String(d.getDate()).padStart(2, '0')
	return `${y}-${m}-${day}`
}

const bumpIfPast = iso => (iso && iso < todayIso() ? todayIso() : iso)

const toTrimmedString = (v, max = 200) => {
	if (typeof v !== 'string') return ''
	return v.slice(0, max)
}

const sanitizeFilters = raw => {
	const f = { ...emptyFilters(), ...(raw || {}) }
	const originLat = toFiniteNumberOrNull(f.originLat)
	const originLng = toFiniteNumberOrNull(f.originLng)
	const destLat = toFiniteNumberOrNull(f.destinationLat)
	const destLng = toFiniteNumberOrNull(f.destinationLng)

	return {
		origin: toTrimmedString(f.origin),
		originLat: originLat != null && originLat >= -90 && originLat <= 90 ? originLat : null,
		originLng: originLng != null && originLng >= -180 && originLng <= 180 ? originLng : null,
		originDh: toFiniteNumberOrNull(f.originDh) ?? 50,
		destination: toTrimmedString(f.destination),
		destinationLat: destLat != null && destLat >= -90 && destLat <= 90 ? destLat : null,
		destinationLng: destLng != null && destLng >= -180 && destLng <= 180 ? destLng : null,
		destinationDh: toFiniteNumberOrNull(f.destinationDh) ?? 150,
		equipment: sanitizeEquipment(f.equipment),
		source: toTrimmedString(f.source, 50) || 'all',
		sort: VALID_SORT.has(f.sort) ? f.sort : 'pickup',
		pickDateFrom: bumpIfPast(toIsoDateOrEmpty(f.pickDateFrom)),
		pickDateTo: bumpIfPast(toIsoDateOrEmpty(f.pickDateTo)),
		maxWeight: toNumericStringOrEmpty(f.maxWeight),
		minDistance: toNumericStringOrEmpty(f.minDistance),
		maxDistance: toNumericStringOrEmpty(f.maxDistance),
		lastSeenHours: toNumericStringOrEmpty(f.lastSeenHours),
		showArchive: Boolean(f.showArchive),
	}
}

const newTabId = () => {
	if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID()
	const rand =
		typeof crypto !== 'undefined' && crypto.getRandomValues
			? Array.from(crypto.getRandomValues(new Uint8Array(8)))
					.map(b => b.toString(16).padStart(2, '0'))
					.join('')
			: Math.random().toString(36).slice(2, 18)
	return `t-${Date.now().toString(36)}-${rand}`
}

const normalizeTab = tab => ({
	id: typeof tab.id === 'string' && tab.id.length > 0 ? tab.id.slice(0, 80) : newTabId(),
	title: toTrimmedString(tab.title, 120) || 'Anywhere',
	subtitle: toTrimmedString(tab.subtitle, 120),
	filters: sanitizeFilters(tab.filters),
})

const sigNum = v => {
	if (v === '' || v == null) return null
	const n = Number(v)
	return Number.isFinite(n) ? n : null
}

const baseSignatureFields = f => [
	sigNum(f.originLat),
	sigNum(f.originLng),
	sigNum(f.originDh),
	sigNum(f.destinationLat),
	sigNum(f.destinationLng),
	sigNum(f.destinationDh),
	String(f.equipment || ''),
	String(f.source || 'all'),
	sigNum(f.maxWeight),
	sigNum(f.minDistance),
	sigNum(f.maxDistance),
	sigNum(f.lastSeenHours),
]

const filterSignature = f =>
	JSON.stringify([
		...baseSignatureFields(f),
		String(f.pickDateFrom || ''),
		String(f.pickDateTo || ''),
	])

export const filterSignatureLoose = f => JSON.stringify(baseSignatureFields(f))

const isEmptyFilters = f => {
	if (f.originLat != null && f.originLng != null) return false
	if (f.destinationLat != null && f.destinationLng != null) return false
	if (f.equipment) return false
	if (f.source && f.source !== 'all') return false
	if (Number(f.maxWeight) > 0) return false
	if (Number(f.minDistance) > 0) return false
	if (Number(f.maxDistance) > 0) return false
	if (Number(f.lastSeenHours) > 0) return false
	return true
}

const DUPLICATE_TAB_MESSAGE = 'This search is already open in another tab'

const defaultPickDate = () => {
	const d = new Date()
	if (d.getHours() * 60 + d.getMinutes() >= 12 * 60 + 30) {
		d.setDate(d.getDate() + 1)
	}
	const y = d.getFullYear()
	const m = String(d.getMonth() + 1).padStart(2, '0')
	const day = String(d.getDate()).padStart(2, '0')
	return `${y}-${m}-${day}`
}

const makeTab = (overrides = {}) => {
	const pickDate = defaultPickDate()
	const baseFilters = {
		...emptyFilters(),
		pickDateFrom: pickDate,
		pickDateTo: pickDate,
	}
	const { filters: overrideFilters, title, subtitle } = overrides
	return {
		id: newTabId(),
		title: title || 'Anywhere',
		subtitle: subtitle || '',
		filters: overrideFilters ? { ...baseFilters, ...overrideFilters } : baseFilters,
	}
}

const loadFromStorage = () => {
	try {
		const raw = localStorage.getItem(STORAGE_KEY)
		if (!raw) return null
		const parsed = JSON.parse(raw)
		if (!parsed || !Array.isArray(parsed.tabs) || parsed.tabs.length === 0) return null
		return { ...parsed, tabs: parsed.tabs.map(normalizeTab) }
	} catch {
		return null
	}
}

// Owns the tab list + active tab id, persists both to localStorage so the
// user's open searches survive a page reload. Filter editing wires up later.
export default function useSearchTabs() {
	const [{ tabs, activeTabId }, setState] = useState(() => {
		const stored = loadFromStorage()
		if (stored) return stored
		const first = makeTab()
		return { tabs: [first], activeTabId: first.id }
	})

	// Debounce localStorage writes — filter editing fires state updates on
	// every keystroke; writing synchronously each time is wasted work.
	const persistTimerRef = useRef(null)
	const latestStateRef = useRef({ tabs, activeTabId })
	useEffect(() => {
		latestStateRef.current = { tabs, activeTabId }
	}, [tabs, activeTabId])

	const flushPersist = useCallback(() => {
		if (persistTimerRef.current) {
			clearTimeout(persistTimerRef.current)
			persistTimerRef.current = null
		}
		try {
			localStorage.setItem(STORAGE_KEY, JSON.stringify(latestStateRef.current))
		} catch {
			// localStorage can fail in private mode / over quota — non-fatal.
		}
	}, [])

	useEffect(() => {
		if (persistTimerRef.current) clearTimeout(persistTimerRef.current)
		persistTimerRef.current = setTimeout(flushPersist, PERSIST_DEBOUNCE_MS)
		return () => {
			if (persistTimerRef.current) clearTimeout(persistTimerRef.current)
		}
	}, [tabs, activeTabId, flushPersist])

	// Flush on tab-hide / unmount so an in-flight debounce isn't lost.
	useEffect(() => {
		window.addEventListener('pagehide', flushPersist)
		return () => {
			window.removeEventListener('pagehide', flushPersist)
			flushPersist()
		}
	}, [flushPersist])

	const [error, setError] = useState(null)
	const errorTimerRef = useRef(null)

	const showError = useCallback(msg => {
		if (errorTimerRef.current) clearTimeout(errorTimerRef.current)
		setError(msg)
		errorTimerRef.current = setTimeout(() => setError(null), 2500)
	}, [])

	useEffect(
		() => () => {
			if (errorTimerRef.current) clearTimeout(errorTimerRef.current)
		},
		[],
	)

	// Get Convex mutations for filters
	const { me, loading: meLoading } = useMe()
	const upsertFilterMutation = useMutation(convexApi.filters.upsertFilter)
	const deleteFilterMutation = useMutation(convexApi.filters.deleteFilterByExternal)

	// Sync tab filters to Convex for real-time load matching (best-effort, non-blocking)
	const syncToConvex = useCallback(
		async (filterId, filters) => {
			try {
				// Only sync if filters are non-empty and user is logged in
				if (isEmptyFilters(filters)) {
					return
				}

				if (!me?.id) {
					return
				}

				// Only sync if filter has origin with coordinates
				// Without origin, the filter cannot match loads based on location
				if (!filters.origin || !filters.originLat || !filters.originLng) {
					return
				}

				// Map LoadLine filter format to Convex format
				const convexFilter = {
					token: getConvexToken(), // Pass JWT token
					filterId: filterId,

					// Origin (required)
					origin: filters.origin || undefined,
					originLat: filters.originLat || 0,
					originLng: filters.originLng || 0,
					originRadiusMiles: filters.originDh || 50,

					// Destination (optional)
					destination: filters.destination || undefined,
					destinationLat: filters.destinationLat || undefined,
					destinationLng: filters.destinationLng || undefined,
					destinationRadiusMiles: filters.destinationDh || 150,

					// Filters
					equipment: filters.equipment || undefined,
					source: filters.source || 'all',
					maxWeight: filters.maxWeight ? Number(filters.maxWeight) : undefined,
					minDistance: filters.minDistance ? Number(filters.minDistance) : undefined,
					maxDistance: filters.maxDistance ? Number(filters.maxDistance) : undefined,
					// Day-bound the dates like useLoads does: start-of-day for
					// `from`, END-OF-DAY for `to`. A bare date made Convex treat
					// `to` as midnight and drop every load later that day.
					pickupDateFrom: filters.pickDateFrom
						? new Date(`${filters.pickDateFrom}T00:00:00`).toISOString()
						: undefined,
					pickupDateTo: filters.pickDateTo
						? new Date(`${filters.pickDateTo}T23:59:59.999`).toISOString()
						: undefined,
					lastSyncedWithinHours: filters.lastSeenHours
						? Number(filters.lastSeenHours)
						: undefined,

					// Map sort field: 'pickup' -> 'pickupDate', 'pay' -> 'price', etc.
					sortBy:
						filters.sort === 'pickup'
							? 'pickupDate'
							: filters.sort === 'pay'
								? 'price'
								: filters.sort === 'lastSeen'
									? 'postedAt'
									: 'postedAt',
				}

				// Call Convex mutation directly
				await upsertFilterMutation(convexFilter)
			} catch (err) {
				// Non-fatal: Convex sync is best-effort
			}
		},
		[upsertFilterMutation, me],
	)

	// Delete filter from Convex when tab is closed (best-effort, non-blocking)
	const deleteFromConvex = useCallback(
		async (filterId, filters) => {
			try {
				if (!me?.id) {
					return
				}

				// Only delete if the filter had origin (meaning it was synced)
				// Filters without origin were never synced to Convex
				if (!filters?.origin || !filters?.originLat || !filters?.originLng) {
					return
				}

				await deleteFilterMutation({
					token: getConvexToken(), // Pass JWT token
					filterId: filterId,
				})
			} catch (err) {
				// Non-fatal: Convex delete is best-effort
			}
		},
		[deleteFilterMutation, me],
	)

	// Sync all existing tabs to Convex on mount (when user logs in)
	const hasSyncedRef = useRef(false)
	useEffect(() => {
		if (!me?.id || meLoading || hasSyncedRef.current) return
		hasSyncedRef.current = true

		tabs.forEach(tab => {
			syncToConvex(tab.id, tab.filters)
		})
	}, [me, meLoading, tabs, syncToConvex])

	// Debounced sync of active tab filters to Convex when they change
	const syncTimerRef = useRef(null)
	useEffect(() => {
		// Skip if not logged in or still loading
		if (!me?.id || meLoading) return

		// Clear any pending sync
		if (syncTimerRef.current) {
			clearTimeout(syncTimerRef.current)
		}

		// Debounce sync by 1 second to avoid too many requests while user is typing
		syncTimerRef.current = setTimeout(() => {
			const activeTab = tabs.find(t => t.id === activeTabId)
			if (activeTab) {
				syncToConvex(activeTab.id, activeTab.filters)
			}
		}, 1000)

		return () => {
			if (syncTimerRef.current) {
				clearTimeout(syncTimerRef.current)
			}
		}
	}, [tabs, activeTabId, me, meLoading, syncToConvex])

	const addTab = useCallback(
		(overrides = {}) => {
			const tab = makeTab(overrides)
			setState(prev => {
				if (!isEmptyFilters(tab.filters)) {
					const newSig = filterSignature(tab.filters)
					const existing = prev.tabs.find(t => filterSignature(t.filters) === newSig)
					if (existing) {
						showError(DUPLICATE_TAB_MESSAGE)
						return prev.activeTabId === existing.id
							? prev
							: { ...prev, activeTabId: existing.id }
					}
				}
				if (prev.tabs.length >= MAX_TABS) {
					showError(`Up to ${MAX_TABS} searches allowed`)
					return prev
				}

				// Sync to Convex asynchronously (non-blocking)
				syncToConvex(tab.id, tab.filters)

				return { tabs: [...prev.tabs, tab], activeTabId: tab.id }
			})
		},
		[showError, syncToConvex],
	)

	const closeTab = useCallback(
		tabId => {
			// Get the tab before closing to pass filters to deleteFromConvex
			setState(prev => {
				const tab = prev.tabs.find(t => t.id === tabId)

				// Delete from Convex asynchronously (non-blocking)
				// Pass filters so we can check if it was synced
				if (tab) {
					deleteFromConvex(tabId, tab.filters)
				}

				const idx = prev.tabs.findIndex(t => t.id === tabId)
				if (idx === -1) return prev

				const remaining = prev.tabs.filter(t => t.id !== tabId)
				if (remaining.length === 0) {
					const fresh = makeTab()
					return { tabs: [fresh], activeTabId: fresh.id }
				}

				let nextActiveId = prev.activeTabId
				if (prev.activeTabId === tabId) {
					const fallback = remaining[idx - 1] || remaining[0]
					nextActiveId = fallback.id
				}
				return { tabs: remaining, activeTabId: nextActiveId }
			})
		},
		[deleteFromConvex],
	)

	const setActiveTab = useCallback(tabId => {
		setState(prev => (prev.activeTabId === tabId ? prev : { ...prev, activeTabId: tabId }))
	}, [])

	const updateActiveTab = useCallback(partial => {
		setState(prev => ({
			...prev,
			tabs: prev.tabs.map(t => (t.id === prev.activeTabId ? { ...t, ...partial } : t)),
		}))
	}, [])

	const activeTab = useMemo(
		() => tabs.find(t => t.id === activeTabId) || tabs[0],
		[tabs, activeTabId],
	)

	const activeSignature = useMemo(
		() => (activeTab ? filterSignature(activeTab.filters) : null),
		[activeTab],
	)

	const duplicateOf = useMemo(() => {
		if (!activeTab || !activeSignature) return null
		if (isEmptyFilters(activeTab.filters)) return null
		const match = tabs.find(
			t => t.id !== activeTab.id && filterSignature(t.filters) === activeSignature,
		)
		return match ? { id: match.id, title: match.title } : null
	}, [tabs, activeTab, activeSignature])

	const lastNonDupTabRef = useRef(null)
	useEffect(() => {
		if (duplicateOf || !activeTab) return
		lastNonDupTabRef.current = {
			id: activeTab.id,
			filters: { ...activeTab.filters },
			title: activeTab.title,
			subtitle: activeTab.subtitle,
		}
	}, [activeTab, duplicateOf])

	const cancelDuplicate = useCallback(() => {
		const snap = lastNonDupTabRef.current
		if (!snap) return
		setState(prev => {
			if (snap.id !== prev.activeTabId) return prev
			return {
				...prev,
				tabs: prev.tabs.map(t =>
					t.id === prev.activeTabId
						? { ...t, filters: snap.filters, title: snap.title, subtitle: snap.subtitle }
						: t,
				),
			}
		})
	}, [])

	const switchToDuplicate = useCallback(() => {
		if (!duplicateOf) return
		setState(prev =>
			prev.activeTabId === duplicateOf.id
				? prev
				: { ...prev, activeTabId: duplicateOf.id }
		)
	}, [duplicateOf])

	return {
		tabs,
		activeTab,
		activeTabId,
		addTab,
		closeTab,
		setActiveTab,
		updateActiveTab,
		error,
		duplicateOf,
		cancelDuplicate,
		switchToDuplicate,
	}
}
